import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import supabase from './supabaseClient';
import { slugify } from './slugUtils';

const navigate_to_shop = (seller) => `/shop/${slugify(seller?.business_name || '')}`;

const ListingDetail = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [seller, setSeller] = useState(null);
  const [marketData, setMarketData] = useState({ avg: null, min: null, max: null, count: 0 });
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(null);
  const [msgForm, setMsgForm] = useState({ buyer_name: '', buyer_email: '', message: '' });
  const [msgSending, setMsgSending] = useState(false);
  const [msgSent, setMsgSent] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [msgError, setMsgError] = useState('');
  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await supabase.from('inventory').select('*').eq('id', listingId).single();
      if (!data) { setLoading(false); return; }
      setListing(data);
      setActivePhoto(data.image_url || null);

      const { data: sellerData } = await supabase.from('sellers').select('*').eq('id', data.seller_id).single();
      setSeller(sellerData);

      // Filter by same sale type and size for accurate comparison
      let priceQuery = supabase.from('plants').select('sold_price, sale_size, sale_type')
        .eq('status', 'approved').ilike('common_name', data.common_name);
      if (data.sale_type) priceQuery = priceQuery.ilike('sale_type', data.sale_type);
      if (data.sale_size) priceQuery = priceQuery.eq('sale_size', String(data.sale_size));
      const { data: prices } = await priceQuery.limit(100);
      if (prices && prices.length > 0) {
        const nums = prices.map(p => parseFloat(String(p.sold_price).replace(/[^0-9.]/g,''))).filter(n => !isNaN(n) && n > 0);
        if (nums.length > 0) {
          setMarketData({ avg: nums.reduce((a,b)=>a+b,0)/nums.length, min: Math.min(...nums), max: Math.max(...nums), count: nums.length });
        }
      }
      setLoading(false);
    };
    fetchData();
  }, [listingId]);

  const formatPrice = (val) => {
    if (!val && val !== 0) return '—';
    const num = parseFloat(String(val).replace(/[^0-9.]/g,''));
    return isNaN(num) ? String(val) : '$' + num.toFixed(2);
  };

  const sendMessage = async () => {
    if (!msgForm.buyer_name || !msgForm.buyer_email || !msgForm.message) { setMsgError('Please fill in all fields.'); return; }
    setMsgSending(true); setMsgError('');
    const { error } = await supabase.from('messages').insert({ listing_id: listing.id, seller_id: listing.seller_id, buyer_name: msgForm.buyer_name, buyer_email: msgForm.buyer_email, message: msgForm.message });
    if (error) setMsgError('Failed to send. Please try again.');
    else setMsgSent(true);
    setMsgSending(false);
  };

  const listingPrice = listing ? parseFloat(String(listing.price||'').replace(/[^0-9.]/g,'')) : null;
  const isFairPrice = marketData.avg && listingPrice && Math.abs(listingPrice - marketData.avg) / marketData.avg <= 0.25;

  const inp = { width: '100%', padding: '11px 14px', border: '1.5px solid #c4a882', borderRadius: '10px', fontSize: '14px', fontFamily: 'Lato, sans-serif', boxSizing: 'border-box', outline: 'none' };

  if (loading) return <div style={{ textAlign: 'center', padding: '80px', fontFamily: 'Lato, sans-serif', color: '#4a7c59' }}>Loading listing...</div>;
  if (!listing) return <div style={{ textAlign: 'center', padding: '80px', fontFamily: 'Lato, sans-serif', color: '#4a5e4b' }}>Listing not found.</div>;

  const photos = [listing.image_url, listing.image_url_2, listing.image_url_3].filter(Boolean);
  const displayPhoto = activePhoto || photos[0];
  const location = listing.location || [seller?.location_city, seller?.location_state].filter(Boolean).join(', ') || '—';

  return (
    <>
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '16px 16px 80px' : '40px 24px 80px' }}>
      <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/for-sale'); }} style={{ background: 'transparent', border: 'none', color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontSize: '13px', fontWeight: 700, cursor: 'pointer', marginBottom: '20px', padding: 0 }}>← Back</button>

      {/* Breadcrumb */}
      <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#8b9e8c', marginBottom: '24px' }}>
        <span style={{ cursor: 'pointer' }} onClick={() => navigate('/for-sale')}>Marketplace</span>
        {seller?.business_name && <><span> / </span><span style={{ cursor: 'pointer' }} onClick={() => navigate(navigate_to_shop(seller))}>{seller.business_name}</span></>}
        <span> / {listing.common_name}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '55% 1fr', gap: '40px', alignItems: 'start' }}>

        {/* LEFT COLUMN */}
        <div>
          {/* Photo Gallery */}
          <div style={{ display: 'flex', gap: '12px' }}>
            {photos.length > 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {photos.map((p, i) => (
                  <img key={i} src={p} alt={`View ${i+1}`} onClick={() => setActivePhoto(p)}
                    style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', border: `2px solid ${displayPhoto === p ? '#4a7c59' : 'transparent'}`, opacity: displayPhoto === p ? 1 : 0.65 }} />
                ))}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ position: 'relative' }}>
              {displayPhoto ? (
                <img src={displayPhoto} alt={listing.common_name} style={{ width: '100%', borderRadius: '16px', objectFit: 'cover', maxHeight: '460px', display: 'block', boxShadow: '0 4px 24px rgba(44,74,46,0.12)' }} />
              ) : (
                <div style={{ width: '100%', height: '400px', borderRadius: '16px', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '80px' }}>🌿</div>
              )}
              {isFairPrice && (
                <img src="/trueplantvaluebadge.png" alt="Verified True Plant Value" style={{ position: 'absolute', top: '12px', right: '12px', width: '80px', height: '80px', pointerEvents: 'none' }} />
              )}
            </div>
            </div>
          </div>

          {/* Plant name + price on mobile */}
          {isMobile && (
            <div style={{ marginTop: '20px', marginBottom: '8px' }}>
              <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '26px', color: '#1e3d20', marginBottom: '8px' }}>{listing.common_name}</h1>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '32px', color: '#1e3d20', fontWeight: 700 }}>{formatPrice(listing.price)}</div>
            </div>
          )}

          {/* Product Description */}
          {listing.notes && (
            <div style={{ marginTop: '32px' }}>
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1e3d20', marginBottom: '12px' }}>Product description</h2>
              <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '14px', color: '#1e3d20', lineHeight: 1.8 }}>{listing.notes}</p>
            </div>
          )}

          {/* Shipping & Policies */}
          <div style={{ marginTop: '32px', borderTop: '1px solid rgba(143,185,154,0.3)', paddingTop: '24px' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1e3d20', marginBottom: '16px' }}>Shipping &amp; policies</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { icon: '📅', label: 'Estimated shipping date', val: listing.estimated_shipping || 'To be calculated' },
                { icon: '📍', label: 'Ships from', val: location },
                { icon: '🚚', label: 'Shipping fee', val: listing.shipping_fee || 'To be calculated' },
              ].map(({ icon, label, val }) => (
                <div key={label} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>{icon}</span>
                  <div>
                    <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b' }}>{label}: </span>
                    <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#1e3d20', fontWeight: 700 }}>{val}</span>
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>💱</span>
                <div>
                  <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b', marginBottom: '4px' }}>Returns and exchanges:</div>
                  <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#1e3d20' }}>
                    {listing.returns_accepted ? '· Returns and exchanges are accepted within 3 days.' : '· Returns and exchanges are not accepted.'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Market Price Comparison */}
          {marketData.count >= 3 && (
            <div style={{ marginTop: '32px', borderTop: '1px solid rgba(143,185,154,0.3)', paddingTop: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                <div>
                  <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1e3d20', margin: 0 }}>📊 Market Price Data</h2>
                  <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '11px', color: '#8b9e8c', marginTop: '2px' }}>
                    Comparing {[listing.sale_size ? `${listing.sale_size}"` : null, listing.sale_type].filter(Boolean).join(' ')} sales of this plant
                  </div>
                </div>
                <button onClick={() => navigate(`/plant/${slugify(listing.common_name)}`)} style={{ background: 'transparent', border: 'none', color: '#4a7c59', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif' }}>Full history →</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '12px' }}>
                {[{ label: 'Avg', val: marketData.avg }, { label: 'Low', val: marketData.min }, { label: 'High', val: marketData.max }].map(({ label, val }) => (
                  <div key={label} style={{ background: '#f7f3ec', borderRadius: '10px', padding: '10px', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '10px', color: '#8b9e8c', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>{label}</div>
                    <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1e3d20', fontWeight: 700 }}>{formatPrice(val?.toFixed(2))}</div>
                  </div>
                ))}
              </div>
              {isFairPrice ? (
                <div style={{ background: '#f0f7f1', borderRadius: '10px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>✓</span>
                  <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#2c4a2e', fontWeight: 700 }}>True Plant Value verified — within 25% of market average</div>
                </div>
              ) : listingPrice && marketData.avg ? (
                <div style={{ background: '#fdf8f0', borderRadius: '10px', padding: '10px 14px', fontFamily: 'Lato, sans-serif', fontSize: '12px', color: '#8b6914' }}>
                  This listing is {listingPrice > marketData.avg ? 'above' : 'below'} market average by {Math.round(Math.abs(listingPrice - marketData.avg) / marketData.avg * 100)}%
                </div>
              ) : null}
              <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '11px', color: '#8b9e8c', marginTop: '6px' }}>Based on {marketData.count} recorded sales</div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ position: isMobile ? 'static' : 'sticky', top: '80px' }}>
          {/* Plant name + price on desktop */}
          {!isMobile && (
            <>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                {listing.sale_type && <span style={{ background: '#f0f7f1', color: '#4a7c59', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 700, fontFamily: 'Lato, sans-serif', textTransform: 'uppercase' }}>{listing.sale_type}</span>}
                {listing.sale_size && <span style={{ background: '#f7f3ec', color: '#8b6914', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 700, fontFamily: 'Lato, sans-serif' }}>{listing.sale_size}" pot</span>}
                {isFairPrice && <span style={{ background: 'linear-gradient(135deg, #1e3d20, #4a7c59)', color: '#f5f0e8', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 700, fontFamily: 'Lato, sans-serif' }}>✓ True Plant Value</span>}
              </div>
              <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '28px', color: '#1e3d20', marginBottom: '6px', lineHeight: 1.2 }}>{listing.common_name}</h1>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '40px', color: '#1e3d20', fontWeight: 700, marginBottom: '6px' }}>{formatPrice(listing.price)}</div>
              {listing.shipping_fee && <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#8b9e8c', marginBottom: '4px' }}>🚚 Shipping fee: <strong>{listing.shipping_fee}</strong></div>}
              {location !== '—' && <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#8b9e8c', marginBottom: '16px' }}>📍 Ships from: <strong>{location}</strong></div>}
            </>
          )}

          {/* Contact / Message */}
          <div style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 20px rgba(44,74,46,0.1)', border: '1px solid rgba(143,185,154,0.3)', marginBottom: '16px' }}>
            {msgSent ? (
              <div style={{ textAlign: 'center', padding: '16px' }}>
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>✅</div>
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1e3d20', marginBottom: '6px' }}>Message Sent!</p>
                <p style={{ fontFamily: 'Lato, sans-serif', fontSize: '13px', color: '#4a5e4b' }}>The seller will reply to your email.</p>
              </div>
            ) : (
              <>
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1e3d20', marginBottom: '14px' }}>💬 Contact Seller</h3>
                {[{ label: 'Your Name', key: 'buyer_name', placeholder: 'Jane Smith' }, { label: 'Your Email', key: 'buyer_email', placeholder: 'your@email.com' }].map(({ label, key, placeholder }) => (
                  <div key={key} style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700, marginBottom: '4px', fontFamily: 'Lato, sans-serif' }}>{label}</label>
                    <input value={msgForm[key]} onChange={(e) => setMsgForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder} style={inp} />
                  </div>
                ))}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase', color: '#4a7c59', fontWeight: 700, marginBottom: '4px', fontFamily: 'Lato, sans-serif' }}>Message</label>
                  <textarea value={msgForm.message} onChange={(e) => setMsgForm(f => ({ ...f, message: e.target.value }))} placeholder="Hi, is this still available?" rows={3} style={{ ...inp, resize: 'vertical' }} />
                </div>
                {msgError && <p style={{ color: '#c0392b', fontSize: '12px', fontFamily: 'Lato, sans-serif', marginBottom: '8px' }}>{msgError}</p>}
                <button onClick={sendMessage} disabled={msgSending} style={{ width: '100%', padding: '14px', background: msgSending ? '#8fb99a' : 'linear-gradient(135deg, #2c4a2e, #4a7c59)', color: '#f5f0e8', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 700, cursor: msgSending ? 'not-allowed' : 'pointer', fontFamily: 'Lato, sans-serif', letterSpacing: '1px' }}>
                  {msgSending ? 'Sending...' : '💬 Send Message'}
                </button>
                {seller?.palmstreet_url && (
                  <a href={seller.palmstreet_url.startsWith('http') ? seller.palmstreet_url : `https://${seller.palmstreet_url}`} target="_blank" rel="noreferrer"
                    style={{ display: 'block', textAlign: 'center', marginTop: '10px', padding: '12px', background: '#f0f7f1', border: '1px solid #4a7c59', color: '#4a7c59', borderRadius: '12px', fontSize: '13px', fontWeight: 700, fontFamily: 'Lato, sans-serif', textDecoration: 'none' }}>
                    View on Palmstreet →
                  </a>
                )}
              </>
            )}
          </div>

          {/* Seller card */}
          {seller && (
            <div onClick={() => navigate(navigate_to_shop(seller))}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#fff', borderRadius: '14px', padding: '14px 16px', border: '1px solid rgba(143,185,154,0.3)', cursor: 'pointer', boxShadow: '0 2px 10px rgba(44,74,46,0.06)' }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = '#4a7c59'}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(143,185,154,0.3)'}
            >
              {seller.profile_photo_url ? (
                <img src={seller.profile_photo_url} alt={seller.business_name} style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'linear-gradient(135deg, #2c4a2e, #4a7c59)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>🌿</div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: '15px', color: '#1e3d20', fontWeight: 700 }}>{seller.business_name}</div>
                {seller.tagline && <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '11px', color: '#4a5e4b' }}>{seller.tagline}</div>}
                {location !== '—' && <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '11px', color: '#8b9e8c' }}>📍 {location}</div>}
              </div>
              <span style={{ color: '#4a7c59', fontFamily: 'Lato, sans-serif', fontSize: '12px', fontWeight: 700, flexShrink: 0 }}>Shop →</span>
            </div>
          )}

          <button onClick={() => navigate(`/plant/${slugify(listing.common_name)}`)}
            style={{ width: '100%', marginTop: '12px', padding: '12px', background: 'transparent', border: '2px solid #4a7c59', color: '#4a7c59', borderRadius: '12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif', textTransform: 'uppercase', letterSpacing: '1px' }}>
            📊 View Price History →
          </button>

          <button id="copy-marketplace-btn" onClick={() => {
            const price = listing.price ? '$' + parseFloat(String(listing.price).replace(/[^0-9.]/g,'')).toFixed(2) : '';
            const details = [listing.sale_type, listing.sale_size ? `${listing.sale_size}"` : null].filter(Boolean).join(' ');
            const location = listing.location || [seller?.location_city, seller?.location_state].filter(Boolean).join(', ') || '';
            const url = `https://trueplantvalue.com/listing/${listing.id}/${listing.sale_type ? listing.sale_type.toLowerCase().replace(/[^a-z0-9]+/g,'-') : ''}${listing.sale_size ? '-'+listing.sale_size+'in' : ''}-${(listing.common_name||'').toLowerCase().replace(/[^a-z0-9]+/g,'-')}`;
            const lines = [
              `🌿 ${listing.common_name} — ${price}`,
              details ? `${details}${isFairPrice ? ' | ✓ True Plant Value Verified' : ''}` : null,
              '',
              listing.notes || null,
              listing.shipping_fee ? `Shipping: ${listing.shipping_fee}` : null,
              location ? `Ships from: ${location}` : null,
              listing.estimated_shipping ? `Ships: ${listing.estimated_shipping}` : null,
              '',
              'View full listing & contact seller:',
              url,
            ];
            const text = lines.filter(l => l !== null).join('\n').replace(/\n{3,}/g, '\n\n').trim();
            navigator.clipboard.writeText(text).then(() => { setShowCopied(true); setTimeout(() => setShowCopied(false), 2500); });
          }}
            style={{ width: '100%', marginTop: '8px', padding: '12px', background: 'transparent', border: '2px solid #8b6914', color: '#8b6914', borderRadius: '12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Lato, sans-serif', textTransform: 'uppercase', letterSpacing: '1px', transition: 'all 0.2s' }}>
            📋 Copy for Marketplace
          </button>
        </div>
      </div>
    </div>

      {showCopied && (
        <div style={{ position: 'fixed', bottom: '32px', left: '50%', transform: 'translateX(-50%)', background: '#2c4a2e', color: '#f5f0e8', borderRadius: '30px', padding: '14px 28px', fontSize: '14px', fontWeight: 700, fontFamily: 'Lato, sans-serif', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', zIndex: 9999, display: 'flex', alignItems: 'center', gap: '10px' }}>
          Copied to clipboard!
        </div>
      )}
    </>
  );
};

export default ListingDetail;